import { faUserPen } from '@fortawesome/pro-light-svg-icons';
import { faArrowLeft } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import { useColorScheme } from '@mui/material/styles';
import classNames from 'classnames';
import { useAtom, useAtomValue } from 'jotai';
import { createSearchParams, useNavigate } from 'react-router';

import VMPGenericChip from '@/components/VMPGenericChip';
import {
  ASSET_TOOLTIP_TEXT,
  assetsFiltersAtom,
  assetsTimeFilterAtom,
  DEFAULT_ASSET_FIELDS,
  selectedAssetAtom
} from '@/pages/Assets';
import type { CriticalityTypes } from '@/pages/VulnerabilityManagement/VulnerabilityManagement.types';
import isLayoutMobileAtom from '@/store/layout';

import { parseSearchFiltersToQueryParams } from '../SearchBar';
import useStyles from './AssetSubTopbarBottom.styles';
import { getSubTopBarBottomData } from './AssetSubTopbarBottom.utils';

const AssetSubTopbarBottom = () => {
  const isLayoutMobile = useAtomValue(isLayoutMobileAtom);
  const { mode: themeMode } = useColorScheme();
  const { classes } = useStyles({ isLayoutMobile, themeMode });
  const selectedAsset = useAtomValue(selectedAssetAtom);
  const navigate = useNavigate();
  const [assetTimeFilter] = useAtom(assetsTimeFilterAtom);
  const [assetsFilters] = useAtom(assetsFiltersAtom);

  const protectedAttributes = selectedAsset?.protected_attributes || [];

  const handleBack = () => {
    navigate({
      pathname: '/asset-inventory',
      search: createSearchParams({
        t: assetTimeFilter,
        q: parseSearchFiltersToQueryParams(assetsFilters)
      }).toString()
    });
  };

  const assetInfoMap = getSubTopBarBottomData(selectedAsset);

  return (
    <div
      className={classNames(
        classes.assetDetailsSubTopbar,
        classes.assetDetailsSubTopbarBottom
      )}
    >
      <div className={classes.bottomContent}>
        {!isLayoutMobile && (
          <Button
            className={classes.bottomContentBackButton}
            color="secondary"
            onClick={handleBack}
          >
            <FontAwesomeIcon icon={faArrowLeft} size="lg" />
          </Button>
        )}
        <div className={classes.bottomContentSection}>
          <div className={classes.bottomContentGrid}>
            {assetInfoMap.map((info) => {
              const { label, key, value } = info;
              return (
                <div style={{ marginRight: 16 }} key={key}>
                  <Typography variant="body2">
                    <span className={classes.bottomContentSectionKeys}>
                      {label}:
                    </span>
                  </Typography>
                  {label === 'Status' && (
                    <Tooltip title="Status at time of last scan">
                      <Chip
                        color={value === 'Online' ? 'success' : 'error'}
                        variant="outlined"
                        label={value as string}
                      />
                    </Tooltip>
                  )}
                  {label === 'Criticality' && (
                    <span className={classes.bottomContentSectionValues}>
                      {value === '' || value === '--' ? (
                        '--'
                      ) : (
                        <VMPGenericChip
                          type="filled"
                          value={selectedAsset?.criticality as CriticalityTypes}
                        />
                      )}
                    </span>
                  )}
                  {label !== 'Status' && label !== 'Criticality' && (
                    <Stack
                      direction="row"
                      sx={{ alignItems: 'center' }}
                      spacing={1}
                    >
                      <span className={classes.bottomContentSectionValues}>
                        {value as string}
                      </span>
                      {key in DEFAULT_ASSET_FIELDS &&
                        protectedAttributes.includes(key) && (
                          <Tooltip title={ASSET_TOOLTIP_TEXT}>
                            <FontAwesomeIcon size="xs" icon={faUserPen} />
                          </Tooltip>
                        )}
                    </Stack>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className={classes.bottomContentGrid} />
      </div>
    </div>
  );
};

export default AssetSubTopbarBottom;
